import React, { useState, useEffect, useRef, useCallback } from "react";
import Navbar from "../components/Navbar";
import { Button } from "react-bootstrap";
import SearchIcon from "@mui/icons-material/Search";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { styled } from "@mui/material/styles";
import InputBase from "@mui/material/InputBase";
import { supabase } from "../supabaseClient";

// Design system colors (matching CSS variables)
const colors = {
  bgPrimary: "#000",
  bgTertiary: "#505050",
  bgTertiaryHover: "#606060",
  borderLight: "rgba(255, 255, 255, 0.2)",
  borderLightHover: "rgba(255, 255, 255, 0.3)",
  borderLightFocus: "rgba(255, 255, 255, 0.5)",
  textPrimary: "#fff",
  textWhite70: "rgba(255, 255, 255, 0.7)",
  textWhite60: "rgba(255, 255, 255, 0.6)",
  shadowTooltip: "0 4px 12px rgba(0, 0, 0, 0.3)",
  radiusXl: 20,
};

// Search bar styled components
const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: colors.radiusXl,
  backgroundColor: colors.bgTertiary,
  color: colors.textPrimary,
  display: "flex",
  alignItems: "center",
  minHeight: "48px",
  border: `1px solid ${colors.borderLight}`,
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: colors.bgTertiaryHover,
    borderColor: colors.borderLightHover,
  },
  "&:focus-within": {
    backgroundColor: colors.bgTertiaryHover,
    borderColor: colors.borderLightFocus,
    boxShadow: "0 0 0 2px rgba(255, 255, 255, 0.1)",
  },
  marginLeft: 0,
  width: "100%",
  maxWidth: "500px",
  margin: "0 auto",
  [theme.breakpoints.down("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  right: 0,
  top: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1,
  cursor: "pointer",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: colors.textPrimary,
  width: "100%",
  display: "flex",
  alignItems: "center",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1.5, 1, 1.5, 0),
    paddingLeft: theme.spacing(2),
    paddingRight: `calc(1em + ${theme.spacing(4)})`,
    color: colors.textPrimary,
    lineHeight: "1.5",
    "&::placeholder": {
      color: colors.textWhite60,
      opacity: 1,
    },
    "&:focus": {
      color: colors.textPrimary,
    },
  },
}));

export default function HomePage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [allSearchData, setAllSearchData] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Fetch all search data on mount
  useEffect(() => {
    const fetchSearchData = async () => {
      try {
        const { data, error } = await supabase
          .from('form_responses')
          .select('*');

        if (error) throw error;
        setAllSearchData(data || []);
      } catch (error) {
        console.error("Error fetching search data:", error);
      }
    };
    fetchSearchData();
  }, []);

  // Load initial search term from localStorage and URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryParam = searchParams.get("q");

    if (queryParam) {
      setSearchTerm(queryParam);
      localStorage.setItem("searchTerm", queryParam);
    } else {
      const storedSearchTerm = localStorage.getItem("searchTerm");
      if (storedSearchTerm) {
        setSearchTerm(storedSearchTerm);
      }
    }
  }, [location.search]);

  // Debounced search function
  const performSearch = useCallback((term) => {
    if (!term || term.trim() === "") {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const searchTermLower = term.toLowerCase().trim();
    const filtered = allSearchData.filter((item) => {
      const title = (item.title || "").toLowerCase();
      const country = (item.country || "").toLowerCase();
      const continent = (item.continent || "").toLowerCase();
      return (
        title.includes(searchTermLower) ||
        country.includes(searchTermLower) ||
        continent.includes(searchTermLower)
      );
    });

    // Helper function to check if a value starts with search term
    const startsWith = (value) => value.toLowerCase().startsWith(searchTermLower);
    const includes = (value) => value.toLowerCase().includes(searchTermLower);

    // Helper function to sort by priority (starts with first, then contains)
    const sortByPriority = (arr) => {
      return arr.sort((a, b) => {
        const aStarts = startsWith(a.displayValue);
        const bStarts = startsWith(b.displayValue);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        return 0; // Keep original order if both start or both don't start
      });
    };

    // 1. Group Cities by exact Title matches
    const cityMap = new Map();
    filtered.forEach((item) => {
      const title = item.title || "";
      if (title && includes(title)) {
        if (!cityMap.has(title)) {
          cityMap.set(title, {
            displayValue: title,
            searchTerm: title,
            type: "city",
            items: [],
          });
        }
        cityMap.get(title).items.push(item);
      }
    });
    const citySuggestions = sortByPriority(Array.from(cityMap.values())).slice(0, 8);

    // 2. Group Countries by Country field
    const countryMap = new Map();
    filtered.forEach((item) => {
      const country = item.country || "";
      if (country && includes(country)) {
        if (!countryMap.has(country)) {
          countryMap.set(country, {
            displayValue: country,
            searchTerm: country,
            type: "country",
            items: [],
          });
        }
        countryMap.get(country).items.push(item);
      }
    });
    const countrySuggestions = sortByPriority(Array.from(countryMap.values())).slice(0, 8);

    // 3. Group Continents by Continent field
    const continentMap = new Map();
    filtered.forEach((item) => {
      const continent = item.continent || "";
      if (continent && includes(continent)) {
        if (!continentMap.has(continent)) {
          continentMap.set(continent, {
            displayValue: continent,
            searchTerm: continent,
            type: "continent",
            items: [],
          });
        }
        continentMap.get(continent).items.push(item);
      }
    });
    const continentSuggestions = sortByPriority(Array.from(continentMap.values())).slice(0, 8);

    // Combine all three categories and prioritize by "starts with"
    const allSuggestions = [
      ...citySuggestions,
      ...countrySuggestions,
      ...continentSuggestions,
    ].sort((a, b) => {
      const aStarts = startsWith(a.displayValue);
      const bStarts = startsWith(b.displayValue);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return 0; // Keep original order if both start or both don't start
    }).slice(0, 8); // Limit total to 8 suggestions

    setSuggestions(allSuggestions);
    setShowSuggestions(allSuggestions.length > 0);
    setSelectedIndex(-1);
  }, [allSearchData]);

  // Debounce search with 300ms delay
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      performSearch(searchTerm);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm, performSearch]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setShowSuggestions(true);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (event) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (event.key === "Enter") {
        triggerSearch(searchTerm);
      }
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          const selected = suggestions[selectedIndex];
          const searchValue = selected.searchTerm || selected.displayValue;
          setSearchTerm(searchValue);
          triggerSearch(searchValue);
          setShowSuggestions(false);
        } else {
          triggerSearch(searchTerm);
        }
        break;
      case "Escape":
        event.preventDefault();
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSuggestionClick = (suggestion) => {
    // Use the searchTerm based on type (Title for city, Country for country, Continent for continent)
    const searchValue = suggestion.searchTerm || suggestion.displayValue;
    setSearchTerm(searchValue);
    triggerSearch(searchValue);
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const triggerSearch = (term) => {
    localStorage.setItem("searchTerm", term);

    // If on homepage and search term exists, navigate to segments page
    if (location.pathname === "/" && term && term.trim() !== "") {
      navigate(`/segments?q=${encodeURIComponent(term)}`);
    } else {
      // Update URL with search term or remove query parameter if empty
      const currentPath = location.pathname;
      if (term && term.trim() !== "") {
        navigate(`${currentPath}?q=${encodeURIComponent(term)}`);
      } else {
        navigate(currentPath);
      }
    }

    // Dispatch custom event for Hero component to listen to
    window.dispatchEvent(
      new CustomEvent("searchTermChanged", {
        detail: { searchTerm: term },
      })
    );
  };

  const handleSearchClick = () => {
    triggerSearch(searchTerm);
  };


  return (
    <div style={{
      backgroundColor: colors.bgPrimary,
      color: colors.textPrimary,
      marginTop: 0,
      paddingTop: 0,
      backgroundImage: 'url(/homeAssets/heroBg.png)',
      backgroundRepeat: 'repeat-x',
      backgroundPosition: 'center top',
      backgroundSize: '1300px auto'
    }}>
      <Navbar />
      <div className="container-fluid p-0 text-white" style={{ marginTop: 0, paddingTop: 0 }}>
        <div className=" w-full hero-section flex items-center justify-center text-2xl text-white font-bold ">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              paddingTop: "100px",
              margin: 0,
              width: "100%",
            }}
          >
            <h1 style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0px",
              color: colors.textPrimary,
              fontSize: "2.5rem",
              fontWeight: "bold"
            }}>
              Berlin Wall Archive
            </h1>
            <p
              style={{
                fontSize: "1.2rem",
                fontWeight: "normal",
                margin: "20px 0 40px 0",
                textAlign: "center",
                maxWidth: "800px",
                padding: "0 20px",
                color: colors.textPrimary,
              }}
            >
              An Interactive Data Archive Mapping the Global Dispersal of the
              Berlin Wall After 1989.
            </p>
            <div
              ref={searchRef}
              style={{ width: "100%", maxWidth: "500px", padding: "0 20px", position: "relative" }}
            >
              <Search>
                <StyledInputBase
                  placeholder="Search"
                  inputProps={{ "aria-label": "search" }}
                  value={searchTerm}
                  onChange={handleSearch}
                  onKeyDown={handleKeyDown}
                  onFocus={() => {
                    if (suggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                />
                <SearchIconWrapper onClick={handleSearchClick}>
                  <SearchIcon style={{ color: "white" }} />
                </SearchIconWrapper>
              </Search>
              {showSuggestions && suggestions.length > 0 && (
                <div
                  ref={suggestionsRef}
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: "20px",
                    right: "20px",
                    marginTop: "8px",
                    backgroundColor: colors.bgTertiary,
                    borderRadius: colors.radiusXl,
                    border: `1px solid ${colors.borderLight}`,
                    maxHeight: "300px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: colors.shadowTooltip,
                  }}
                >
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={`${suggestion.type}-${suggestion.displayValue}-${index}`}
                      onClick={() => handleSuggestionClick(suggestion)}
                      onMouseEnter={() => setSelectedIndex(index)}
                      style={{
                        padding: "12px 16px",
                        cursor: "pointer",
                        color: colors.textPrimary,
                        backgroundColor: index === selectedIndex ? colors.bgTertiaryHover : "transparent",
                        borderBottom: index < suggestions.length - 1 ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
                        transition: "background-color 0.2s",
                        borderRadius: index === 0 ? `${colors.radiusXl}px ${colors.radiusXl}px 0 0` : index === suggestions.length - 1 ? `0 0 ${colors.radiusXl}px ${colors.radiusXl}px` : "0",
                      }}
                    >
                      <div style={{ fontSize: "12px", color: colors.textWhite70 }}>
                        {suggestion.displayValue}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="d-flex flex-column flex-lg-row align-items-center justify-content-between w-100 px-5 container gap-4">
          <section
            className="text-white my-5"
            style={{ width: "100%", maxWidth: "500px" }}
          >
            <h1 className="fw-bold text-start">Mission</h1>
            <div className="text-white fs-4 mt-4 text-base">
              The Berlin Wall Archive is a digital art initiative dedicated to
              chronicling the transformative journey of a structure that once
              split a city and a nation. Originally constructed to divide East
              and West Berlin, the wall now finds new meaning through the
              artistic reinterpretation of its fragments around the world.
            </div>
            <Link
              to="/segments"
              variant="dark"
              size="sm"
              className="mt-3 d-flex align-items-center gap-2 rounded-2 py-2 px-3 fs-5 fw-bold"
              style={{
                backgroundColor: "transparent",
                border: "none",
                textDecoration: "none",
                color: "white",
              }}
            >
              <SearchIcon fontSize="small" />
              <span>Explore More</span>
            </Link>
          </section>
          <div className="my-3 my-lg-0">
            <img
              src="/homeAssets/mission_img.png"
              style={{ height: "300px" }}
              className="w-auto"
              alt=""
            />
          </div>
        </div>

        <div className="d-flex flex-column-reverse flex-lg-row align-items-center justify-content-between w-100 px-5 container gap-4">
          <div className="mb-3 mb-lg-0">
            <img
              src="/homeAssets/1957 Map Stitch 1.png"
              style={{ height: "300px" }}
              className="w-auto"
              alt=""
            />
          </div>
          <section
            className="text-white my-5"
            style={{ width: "100%", maxWidth: "500px" }}
          >
            <h1 className="fw-bold text-start">Origin</h1>
            <div className="text-white fs-4 mt-4">
              Launched in the fall of 2023, this project set out to capture both
              the images and geographic data of Berlin Wall segments that have
              been dispersed globally. To date, over 500 segments have been
              documented—representing just 0.4% of the original wall's total
              expanse—offering a unique snapshot of history remained.
            </div>
          </section>
        </div>

        <div className="d-flex flex-column flex-lg-row align-items-center justify-content-between w-100 px-5 container gap-4">
          <section
            className="text-white my-5"
            style={{ width: "100%", maxWidth: "700px" }}
          >
            <h1 className="fw-bold text-start">About Me</h1>
            <div className="text-white fs-4 mt-4">
              While living in Southern California, I encountered unexpected
              fragments of the Berlin Wall—from near LACMA and the Reagan
              Presidential Library to the Chapman Campus. A transformative visit
              to Berlin in the summer of 2022 deepened my curiosity and ignited
              a passion to trace the wall's diverse journeys. Inspired by these
              moments, I created a digital space that celebrates the global
              legacy of these historic segments.
            </div>

            <Button
              variant="dark"
              size="sm"
              className="mt-3 d-flex align-items-center gap-2 rounded-2 py-2 px-3 fs-5 fw-bold"
              style={{ backgroundColor: "transparent", border: "none" }}
              onClick={() =>
                window.open("https://markveksler.cargo.site/", "_blank")
              }
            >
              <SearchIcon fontSize="small" />
              <span>Explore More</span>
            </Button>
          </section>
          <div className="my-3 my-lg-0">
            <img
              src="/homeAssets/aboutme.png"
              style={{ height: "400px" }}
              className="w-auto"
              alt=""
            />
          </div>
        </div>
        <div className="w-full">
          <img
            src="/homeAssets/bottomImage.png"
            class="w-full mt-1 img-bottom"
            alt="bottom"
          />
        </div>
      </div>
    </div>
  );
}
